/**
 * Vibration mobile (phase 5) : simple enveloppe autour de `navigator.vibrate`,
 * injectable pour les tests. Aucun état de simulation ici — ce module ne fait
 * qu'exécuter ce qu'on lui demande.
 */
export interface Haptics {
  /** Déclenche une vibration : durée unique (ms) ou motif vibration/pause. */
  vibrate(pattern: number | readonly number[]): void;
  /** Active/désactive la vibration (persisté par l'appelant, pas ici). */
  setEnabled(enabled: boolean): void;
  enabled(): boolean;
  /** `true` si l'appareil expose `navigator.vibrate`. */
  supported(): boolean;
}

export interface HapticsOptions {
  /** `Navigator` injectable pour les tests. `null` = pas de vibration possible. */
  readonly navigator?: Pick<Navigator, 'vibrate'> | null;
  readonly enabled?: boolean;
}

function defaultNavigator(): Pick<Navigator, 'vibrate'> | null {
  return typeof navigator === 'undefined' ? null : navigator;
}

/**
 * Construit un contrôleur de vibrations.
 *
 * `vibrate()` ne lève jamais : certains navigateurs lèvent quand l'appel
 * n'est pas déclenché par un geste utilisateur, l'exception est avalée.
 */
export function createHaptics(options: HapticsOptions = {}): Haptics {
  const nav = options.navigator === undefined ? defaultNavigator() : options.navigator;
  let enabledFlag = options.enabled ?? true;

  const supported = (): boolean => typeof nav?.vibrate === 'function';

  return {
    vibrate(pattern: number | readonly number[]): void {
      if (!enabledFlag || !supported()) {
        return;
      }
      try {
        const value = Array.isArray(pattern) ? [...pattern] : (pattern as number);
        nav?.vibrate(value);
      } catch {
        // vibration hors geste utilisateur, appareil capricieux… : sans conséquence.
      }
    },

    setEnabled(enabled: boolean): void {
      enabledFlag = enabled;
    },

    enabled(): boolean {
      return enabledFlag;
    },

    supported,
  };
}
